import { useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { KILO } from '../constants/KILO_THEME'

interface OnboardingLayoutProps {
  step: number
  totalSteps: number
  children: ReactNode
  onNext?: () => void
  onPrev?: () => void
  isLastStep?: boolean
  isSubmitting?: boolean
  nextButtonText?: string
  /** Overrides step-based header progress (e.g. asymptotic generation bar). */
  headerProgress?: number
}

const OnboardingLayout = ({
  step,
  totalSteps,
  children,
  onNext,
  onPrev,
  isLastStep = false,
  isSubmitting = false,
  nextButtonText,
  headerProgress,
}: OnboardingLayoutProps) => {
  const { t } = useTranslation()
  const progress = headerProgress ?? ((step + 1) / totalSteps) * 100
  const resolvedNextLabel = nextButtonText ?? (isLastStep ? t('onboarding.launchProgramme') : t('common.next'))

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [step])

  return (
    <div className={`min-h-screen ${KILO.bg.page} ${KILO.text.primary}`}>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-kilo-border bg-kilo-page/95 backdrop-blur-md">
        <div className="mx-auto max-w-lg space-y-2 px-4 py-4">
          <p className={KILO.meta}>{t('onboarding.intakeProtocol')}</p>
          <h1 className={`text-lg font-bold tracking-tight ${KILO.text.primary}`}>
            {t('onboarding.stepOf', { current: step + 1, total: totalSteps })}
          </h1>
          <div className={KILO.progressTrack}>
            <div
              className="h-full bg-kilo-accent transition-all duration-1000 ease-out motion-reduce:transition-none"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-lg px-4 pb-32 pt-[7.25rem]">
        <div className={`${KILO.radius.card} ${KILO.border.default} ${KILO.bg.card} p-4`}>{children}</div>
      </div>

      <div className={`fixed bottom-0 left-0 right-0 z-40 border-t border-kilo-border ${KILO.bg.page}/95 p-4 pb-6 backdrop-blur-md`}>
        <div className="mx-auto flex max-w-lg flex-col gap-3">
          <button
            type="button"
            onClick={onNext}
            disabled={isSubmitting}
            className="h-12 w-full rounded-2xl bg-kilo-accent text-base font-bold text-black disabled:opacity-60"
          >
            {isSubmitting ? (isLastStep ? t('onboarding.generatingProgramme') : t('common.working')) : resolvedNextLabel}
          </button>
          {onPrev ? (
            <button
              type="button"
              onClick={() => onPrev?.()}
              disabled={step === 0}
              className={`h-12 w-full rounded-2xl border border-kilo-border bg-kilo-alt text-base font-semibold ${KILO.text.muted} disabled:opacity-50`}
            >
              {t('common.back')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default OnboardingLayout
